import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Checkbox, Dropdown } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import MonthToMonthComparison from "../MonthToMonthComparison/MonthtoMonthComparison";
import useColumnVisibility from "../../../hooks/useColumnVisibility";

const BRAND_COLUMNS_DEFAULT = {
  rank: true,
  brand: true,
  root_category: true,
  category_2: true,
  category_3: true,
  category_4: true,
  sellers_count: true,
  sells: true,
  price: true,
  gmv: true,
};

const BRAND_COLUMN_LABELS = [
  { key: "rank", label: "Ранг" },
  { key: "brand", label: "Бренд" },
  { key: "root_category", label: "Категория 1" },
  { key: "category_2", label: "Категория 2" },
  { key: "category_3", label: "Категория 3" },
  { key: "category_4", label: "Категория 4" },
  { key: "sellers_count", label: "Кол-во продавцов" },
  { key: "sells", label: "Заказы" },
  { key: "price", label: "Средняя цена" },
  { key: "gmv", label: "Общая выручка" },
];

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Ranked breakdown of the categories where a single brand earns the most revenue
const TopCategoriesByRevenue = ({
  categories,
  brandName,
  formatCurrency,
  formatNumber,
}) => {
  const rankStyles = {
    1: { badge: "tw-bg-amber-400 tw-text-amber-900", bar: "#f59e0b", medal: "🥇" },
    2: { badge: "tw-bg-gray-300 tw-text-gray-700", bar: "#9ca3af", medal: "🥈" },
    3: { badge: "tw-bg-orange-300 tw-text-orange-900", bar: "#fb923c", medal: "🥉" },
  };

  const maxGmv = categories.reduce(
    (max, c) => (c.total_gmv > max ? c.total_gmv : max),
    0
  );

  return (
    <div className="row mt-4">
      <div className="col-12">
        <div
          className="tw-rounded-xl tw-overflow-hidden tw-shadow-sm tw-border"
          style={{ borderColor: "#eef0f4" }}
        >
          {/* Header */}
          <div
            className="tw-px-5 tw-py-4"
            style={{
              background:
                "linear-gradient(135deg, #1976d2 0%, #2f6fed 60%, #6d8bff 100%)",
            }}
          >
            <h6 className="tw-mb-0 tw-font-bold tw-text-white tw-text-base tw-flex tw-items-center tw-gap-2">
              <i className="fas fa-trophy"></i>
              Категории с наибольшей выручкой
            </h6>
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] md:tw-text-xs tw-text-white/80">
              Где бренд «{brandName}» зарабатывает больше всего
            </p>
          </div>

          {/* Rows */}
          <div className="tw-bg-white tw-divide-y" style={{ borderColor: "#f1f3f7" }}>
            {categories.map((cat) => {
              const style = rankStyles[cat.rank] || {
                badge: "tw-bg-blue-50 tw-text-[#1976d2]",
                bar: "#cbd5e1",
              };
              const widthPct = maxGmv > 0 ? (cat.total_gmv / maxGmv) * 100 : 0;
              const sharePct = Math.round((cat.gmv_share || 0) * 100);

              return (
                <div
                  key={`${cat.rank}-${cat.category_path}`}
                  className="tw-flex tw-items-center tw-gap-3 md:tw-gap-4 tw-px-4 md:tw-px-5 tw-py-3 tw-transition-colors hover:tw-bg-[#f8fafc]"
                >
                  {/* Rank badge */}
                  <div
                    className={`tw-flex tw-items-center tw-justify-center tw-rounded-full tw-font-bold tw-shrink-0 ${style.badge}`}
                    style={{ width: "38px", height: "38px", fontSize: "13px" }}
                  >
                    {style.medal || `#${cat.rank}`}
                  </div>

                  {/* Category name + bar */}
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
                      <span
                        className="tw-font-semibold tw-text-black tw-text-xs md:tw-text-sm tw-truncate"
                        title={cat.category_path}
                      >
                        {cat.category_path}
                      </span>
                      <span className="tw-text-[10px] md:tw-text-xs tw-font-bold tw-text-[#1976d2] tw-shrink-0">
                        {sharePct}%
                      </span>
                    </div>
                    <div
                      className="tw-w-full tw-rounded-full tw-overflow-hidden"
                      style={{ height: "6px", backgroundColor: "#eef2f7" }}
                    >
                      <div
                        className="tw-h-full tw-rounded-full tw-transition-all"
                        style={{
                          width: `${widthPct}%`,
                          backgroundColor: style.bar,
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Revenue + sells */}
                  <div className="tw-text-right tw-shrink-0" style={{ minWidth: "90px" }}>
                    <div className="tw-font-bold tw-text-black tw-text-xs md:tw-text-sm tw-whitespace-nowrap">
                      {formatCurrency(cat.total_gmv)} ₸
                    </div>
                    <div className="tw-text-[10px] md:tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                      {formatNumber(cat.total_sells_count)} заказов
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const BrandAnalyticsDisplay = ({
  brandData,
  isLoading,
  error,
  to_date,
  onPageChange,
  sortField,
  sortDirection,
  onSort,
  onBrandClick,
}) => {
  console.log("TO DATE IS ", to_date);
  console.log(typeof to_date);
  const navigate = useNavigate();
  const { visibleColumns, toggleColumn } = useColumnVisibility(
    "brand_analytics",
    BRAND_COLUMNS_DEFAULT
  );
  const sellsChartRef = useRef(null);
  const priceChartRef = useRef(null);
  const gmvChartRef = useRef(null);
  const sellersChartRef = useRef(null);

  // Determine if we're in single brand view or multiple brands view
  const isMultipleBrandsView =
    brandData?.brands_aggregated?.results?.length > 1;
  const isSingleBrandView = brandData?.brands_aggregated?.results?.length === 1;

  const formatNumber = (num) => {
    return new Intl.NumberFormat("ru-RU").format(num);
  };

  const get_previous_month = (year, month) => {
    if (month === 1) {
      return { year: year - 1, month: 12 };
    } else {
      return { year, month: month - 1 };
    }
  };

  const toYearMonth = (dateStr) => {
    const [yearStr, monthStr] = dateStr.split("-");
    console.log("DATE STR ", dateStr, yearStr, monthStr);
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    console.log("YEAR AND MONTH", year, month);
    return { year, month };
  };

  const { year: original_year, month: original_month } = toYearMonth(to_date);
  const { year: previous_year, month: previous_month } = get_previous_month(
    original_year,
    original_month
  );

  console.log("ORIGINAL YEAR AND MONTH", original_year, original_month);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getMonthName = (month) => {
    const months = [
      "Янв",
      "Фев",
      "Мар",
      "Апр",
      "Май",
      "Июн",
      "Июл",
      "Авг",
      "Сен",
      "Окт",
      "Ноя",
      "Дек",
    ];
    return months[month - 1];
  };

  const formatDateLabel = (year, month) => {
    return `${getMonthName(month)}. ${year}`;
  };

  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const prepareChartData = () => {
    if (!brandData?.historical_data || brandData.historical_data.length === 0) {
      return { labels: [], sellsData: [], priceData: [], gmvData: [] };
    }

    // Sort data by year and month (oldest first for chart)
    const sortedData = [...brandData.historical_data].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    const labels = sortedData.map((item) =>
      formatDateLabel(item.year, item.month)
    );

    return {
      labels,
      sellsData: sortedData.map((item) => item.sells_count),
      priceData: sortedData.map((item) => item.avg_price),
      gmvData: sortedData.map((item) => item.gmv),
      sellersData: sortedData.map((item) => item.sellers_count || 0),
    };
  };

  const createChart = (
    canvasRef,
    data,
    label,
    color,
    yAxisLabel,
    formatValue
  ) => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");

    // Destroy existing chart if it exists
    if (canvasRef.current.chart) {
      canvasRef.current.chart.destroy();
    }

    canvasRef.current.chart = new ChartJS(ctx, {
      type: "line",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: label,
            data: data.data,
            borderColor: color,
            backgroundColor: color + "20",
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: color,
            pointBorderColor: color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: function (context) {
                const value = context.parsed.y;
                return `${label}: ${formatValue(value)}`;
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
            },
          },
          y: {
            display: true,
            title: {
              display: true,
              text: yAxisLabel,
            },
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return formatValue(value);
              },
            },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
      },
    });
  };

  useEffect(() => {
    if (brandData?.historical_data) {
      const chartData = prepareChartData();

      // Create charts after a short delay to ensure DOM is ready
      setTimeout(() => {
        createChart(
          sellsChartRef,
          { labels: chartData.labels, data: chartData.sellsData },
          "Количество продаж",
          "#3B82F6",
          "Количество",
          formatNumber
        );

        createChart(
          priceChartRef,
          { labels: chartData.labels, data: chartData.priceData },
          "Средняя цена",
          "#10B981",
          "Цена (₸)",
          (value) => formatCurrency(value) + " ₸"
        );

        createChart(
          gmvChartRef,
          { labels: chartData.labels, data: chartData.gmvData },
          "Выручка",
          "#F59E0B",
          "Выручка (₸)",
          (value) => formatCurrency(value) + " ₸"
        );

        createChart(
          sellersChartRef,
          { labels: chartData.labels, data: chartData.sellersData },
          "Количество продавцов",
          "#8B5CF6",
          "Количество",
          formatNumber
        );
      }, 100);
    }
  }, [brandData]);

  useEffect(() => {
    return () => {
      [sellsChartRef, priceChartRef, gmvChartRef, sellersChartRef].forEach(
        (ref) => {
          if (ref.current?.chart) {
            ref.current.chart.destroy();
          }
        }
      );
    };
  }, []);

  const handleSearchProducts = (brandName) => {
    if (brandName) {
      // Navigate to product analytics page with brand filter
      navigate("/product-analytics", {
        state: {
          brandFilter: brandName,
          shouldSearch: true,
        },
      });
    }
  };

  // Helper function to render sortable column header
  const renderSortableHeader = (field, title, className = "") => {
    const isActive = sortField === field;
    const sortIcon = isActive ? (sortDirection === "asc" ? "↑" : "↓") : "↕";

    return (
      <div
        className={`text-center ${className}`}
        style={{
          cursor: "pointer",
          userSelect: "none",
          transition: "background-color 0.2s ease",
          borderRadius: "4px",
          padding: "4px",
        }}
        onClick={() => onSort && onSort(field)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isActive ? "#e3f2fd" : "transparent";
        }}
      >
        <span
          className={`tw-font-bold tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]
    ${isActive ? "tw-text-[#1976d2]" : "tw-text-[#212529]"}
  `}
        >
          {title}
          <span
            className={`
      tw-font-bold tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]
      ${
        isActive
          ? "tw-opacity-100 tw-text-[#1976d2]"
          : "tw-opacity-60 tw-text-[#00072D]"
      }
    `}
          >
            {sortIcon}
          </span>
        </span>
      </div>
    );
  };

  // Pagination component
  const PaginationControls = ({ pagination }) => {
    if (!pagination || pagination.total_pages <= 1) return null;

    const handlePageChange = (newPage) => {
      if (onPageChange && newPage >= 1 && newPage <= pagination.total_pages) {
        onPageChange(newPage);
      }
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={!pagination.has_previous}
            title="Предыдущая страница"
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <span className="mx-3">
            Страница {pagination.current_page} из {pagination.total_pages}
          </span>

          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={!pagination.has_next}
            title="Следующая страница"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    );
  };

  // Render single brand row
  const renderBrandRow = (brand, showRank = true) => (
    <div key={brand.brand} className="col-12 mb-3">
      <div className="card shadow-sm">
        <div className="card-body py-3">
          <div className="tw-flex tw-items-center" style={{ minHeight: "60px" }}>
            {visibleColumns.rank && showRank && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-font-bold tw-text-base tw-text-[#1976d2] tw-bg-[#e3f2fd] tw-py-2 tw-px-3 tw-rounded-full tw-inline-block tw-min-w-10">
                  #{brand.rank}
                </span>
              </div>
            )}
            {visibleColumns.brand && (
              <div className="tw-flex-1 tw-text-center">
                {onBrandClick ? (
                  <button
                    type="button"
                    onClick={() => onBrandClick(brand.brand)}
                    title="Открыть детальную аналитику бренда"
                    className="tw-font-bold tw-text-[#1976d2] tw-text-[8px] md:tw-text-xs tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer hover:tw-underline"
                  >
                    {brand.brand}
                  </button>
                ) : (
                  <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-xs">
                    {brand.brand}
                  </span>
                )}
              </div>
            )}
            {visibleColumns.root_category && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-xs">
                  {brandData.category?.root_category || "-"}
                </span>
              </div>
            )}
            {visibleColumns.category_2 && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-xs">
                  {brandData.category?.category_2 || "-"}
                </span>
              </div>
            )}
            {visibleColumns.category_3 && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-xs">
                  {brandData.category?.category_3 || "-"}
                </span>
              </div>
            )}
            {visibleColumns.category_4 && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-xs">
                  {brandData.category?.category_4 || "-"}
                </span>
              </div>
            )}
            {visibleColumns.sellers_count && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-text-[8px] md:tw-text-xs">-</span>
              </div>
            )}
            {visibleColumns.sells && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-text-[8px] md:tw-text-xs">
                  {formatNumber(brand.total_sells_count)}
                </span>
              </div>
            )}
            {visibleColumns.price && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-text-[8px] md:tw-text-xs">
                  {formatCurrency(Math.floor(brand.avg_price))}
                </span>
              </div>
            )}
            {visibleColumns.gmv && (
              <div className="tw-flex-1 tw-text-center">
                <span className="tw-text-[8px] md:tw-text-xs">
                  {formatCurrency(brand.total_gmv)}
                </span>
              </div>
            )}
            <div style={{ flex: 2 }} className="tw-flex tw-justify-center tw-items-center">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handleSearchProducts(brand.brand)}
                title="Поиск товаров этого бренда"
              >
                <i className="fas fa-search me-1"></i>
                Все товары
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="checkout wizard7 global-shadow border-0 px-sm-50 px-20 pt-sm-50 py-30 mb-30 bg-white radius-xl w-100">
        <div className="text-center">
          <h5 className="text-danger">Ошибка</h5>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="checkout wizard7 global-shadow border-0 px-sm-50 px-20 pt-sm-50 py-30 mb-30 bg-white radius-xl w-100">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <p className="mt-2">Загрузка аналитики бренда...</p>
        </div>
      </div>
    );
  }

  if (!brandData) {
    return (
      <div className="checkout wizard7 global-shadow border-0 px-sm-50 px-20 pt-sm-50 py-30 mb-30 bg-white radius-xl w-100">
        <div className="text-center py-5">
          <h5>Аналитика бренда</h5>
          <p className="text-muted">Выберите бренд для просмотра аналитики</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout wizard7 global-shadow border-0 px-sm-50 px-20 pt-sm-50 py-30 mb-30 bg-white radius-xl w-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5>
          {isSingleBrandView
            ? `Аналитика бренда: ${brandData.brands_aggregated.results[0].brand}`
            : `Аналитика брендов (${brandData.brands_aggregated.pagination.total_count})`}
        </h5>
        <Dropdown
          trigger={["click"]}
          dropdownRender={() => (
            <div
              style={{
                background: "#fff",
                padding: "12px 16px",
                borderRadius: "8px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                minWidth: "180px",
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: "8px",
                  fontSize: "13px",
                  color: "#333",
                }}
              >
                Показать столбцы
              </div>
              {BRAND_COLUMN_LABELS.map(({ key, label }) => (
                <div key={key} style={{ marginBottom: "6px" }}>
                  <Checkbox
                    checked={visibleColumns[key]}
                    onChange={() => toggleColumn(key)}
                  >
                    {label}
                  </Checkbox>
                </div>
              ))}
            </div>
          )}
        >
          <Button size="small" icon={<SettingOutlined />}>
            Столбцы
          </Button>
        </Dropdown>
      </div>

      {/* Column Headers */}
      <div className="tw-sticky tw-top-[56px] md:tw-top-[64px] tw-z-[1000] tw-bg-[#f8f9fa] tw-shadow-sm">
        <div className="row mb-3">
          <div className="col-12">
            <div className="card border" style={{ backgroundColor: "#f8f9fa" }}>
              <div className="card-body py-3">
                <div
                  className="tw-flex tw-items-center"
                  style={{ minHeight: "45px" }}
                >
                  {visibleColumns.rank &&
                    renderSortableHeader("rank", "Ранг", "tw-flex-1")}
                  {visibleColumns.brand && (
                    <div className="tw-flex-1 tw-text-center">
                      <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                        Бренд
                      </span>
                    </div>
                  )}
                  {visibleColumns.root_category && (
                    <div className="tw-flex-1 tw-text-center">
                      <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                        Категория 1
                      </span>
                    </div>
                  )}
                  {visibleColumns.category_2 && (
                    <div className="tw-flex-1 tw-text-center">
                      <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                        Категория 2
                      </span>
                    </div>
                  )}
                  {visibleColumns.category_3 && (
                    <div className="tw-flex-1 tw-text-center">
                      <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                        Категория 3
                      </span>
                    </div>
                  )}
                  {visibleColumns.category_4 && (
                    <div className="tw-flex-1 tw-text-center">
                      <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                        Категория 4
                      </span>
                    </div>
                  )}
                  {visibleColumns.sellers_count && (
                    <div className="tw-flex-1 tw-text-center">
                      <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                        Кол-во продавцов
                      </span>
                    </div>
                  )}
                  {visibleColumns.sells &&
                    renderSortableHeader("sells", "Заказы", "tw-flex-1")}
                  {visibleColumns.price &&
                    renderSortableHeader("price", "Средняя цена", "tw-flex-1")}
                  {visibleColumns.gmv &&
                    renderSortableHeader("gmv", "Общая выручка", "tw-flex-1")}
                  <div style={{ flex: 2 }} className="tw-flex tw-justify-center tw-items-center">
                    <span className="tw-font-bold tw-text-black tw-text-[8px] md:tw-text-[10px] lg:tw-text-[11px]">
                      Действия
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Data Rows */}
      <div className="row">
        {brandData.brands_aggregated.results.map((brand) =>
          renderBrandRow(brand, true)
        )}
      </div>

      {/* Pagination Controls */}
      <PaginationControls pagination={brandData.brands_aggregated.pagination} />

      {/* Top Categories by Revenue - Only for single brand view */}
      {isSingleBrandView &&
        brandData?.top_categories_by_revenue?.length > 0 && (
          <TopCategoriesByRevenue
            categories={brandData.top_categories_by_revenue}
            brandName={brandData.brands_aggregated.results[0].brand}
            formatCurrency={formatCurrency}
            formatNumber={formatNumber}
          />
        )}

      {/* Month-to-Month Comparison Section - Only for single brand view */}
      {isSingleBrandView &&
        brandData &&
        brandData.last_month_stats &&
        brandData.previous_month_stats && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-header bg-white">
                  <h6
                    className="mb-0 fw-bold"
                    style={{ textTransform: "none" }}
                  >
                    Сравнение {formatDateLabel(original_year, original_month)} с{" "}
                    {formatDateLabel(previous_year, previous_month)}
                  </h6>
                </div>

                <div className="card-body">
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <MonthToMonthComparison
                      prev_month_stats={
                        brandData.previous_month_stats.sells_count
                      }
                      current_month_stats={
                        brandData.last_month_stats.sells_count
                      }
                      comparison_name="Продажи"
                      progress_bar_color="tw-bg-green-600"
                      prev_month={formatDateLabel(
                        previous_year,
                        previous_month
                      )}
                      showCurrency={false}
                    />

                    <MonthToMonthComparison
                      prev_month_stats={
                        brandData.previous_month_stats.avg_price
                      }
                      current_month_stats={brandData.last_month_stats.avg_price}
                      comparison_name="Средняя цена"
                      progress_bar_color="tw-bg-blue-600"
                      prev_month={formatDateLabel(
                        previous_year,
                        previous_month
                      )}
                      showCurrency={true}
                    />

                    <MonthToMonthComparison
                      prev_month_stats={brandData.previous_month_stats.gmv}
                      current_month_stats={brandData.last_month_stats.gmv}
                      comparison_name="Выручка"
                      progress_bar_color="tw-bg-red-600"
                      prev_month={formatDateLabel(
                        previous_year,
                        previous_month
                      )}
                      showCurrency={true}
                    />

                    <MonthToMonthComparison
                      prev_month_stats={
                        brandData.previous_month_stats.sellers_count
                      }
                      current_month_stats={
                        brandData.last_month_stats.sellers_count
                      }
                      comparison_name="Количество продавцов"
                      progress_bar_color="tw-bg-yellow-600"
                      prev_month={formatDateLabel(
                        previous_year,
                        previous_month
                      )}
                      showCurrency={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Historical Charts Section - Only for single brand view */}
      {isSingleBrandView && brandData && (
        <div className="tw-space-y-4 tw-mt-4">
          {/* Sales Chart */}
          <div className="tw-w-full">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border">
              <div className="tw-bg-white tw-px-4 tw-py-3 tw-border-b tw-rounded-t-lg">
                <h6 className="tw-mb-0 tw-font-bold tw-text-base">
                  Количество продаж
                </h6>
              </div>
              <div className="tw-p-4">
                {brandData.historical_data &&
                brandData.historical_data.length > 0 ? (
                  <div className="tw-h-80 tw-relative">
                    <canvas ref={sellsChartRef}></canvas>
                  </div>
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-center tw-h-80">
                    <p className="tw-text-gray-500 tw-mb-0">
                      Недостаточно данных для отображения графика
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Price Chart */}
          <div className="tw-w-full">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border">
              <div className="tw-bg-white tw-px-4 tw-py-3 tw-border-b tw-rounded-t-lg">
                <h6 className="tw-mb-0 tw-font-bold tw-text-base">
                  Средняя цена
                </h6>
              </div>
              <div className="tw-p-4">
                {brandData.historical_data &&
                brandData.historical_data.length > 0 ? (
                  <div className="tw-h-80 tw-relative">
                    <canvas ref={priceChartRef}></canvas>
                  </div>
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-center tw-h-80">
                    <p className="tw-text-gray-500 tw-mb-0">
                      Недостаточно данных для отображения графика
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* GMV Chart */}
          <div className="tw-w-full">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border">
              <div className="tw-bg-white tw-px-4 tw-py-3 tw-border-b tw-rounded-t-lg">
                <h6 className="tw-mb-0 tw-font-bold tw-text-base">Выручка</h6>
              </div>
              <div className="tw-p-4">
                {brandData.historical_data &&
                brandData.historical_data.length > 0 ? (
                  <div className="tw-h-80 tw-relative">
                    <canvas ref={gmvChartRef}></canvas>
                  </div>
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-center tw-h-80">
                    <p className="tw-text-gray-500 tw-mb-0">
                      Недостаточно данных для отображения графика
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sellers Count Chart */}
          <div className="tw-w-full">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border">
              <div className="tw-bg-white tw-px-4 tw-py-3 tw-border-b tw-rounded-t-lg">
                <h6 className="tw-mb-0 tw-font-bold tw-text-base">
                  Количество продавцов
                </h6>
              </div>
              <div className="tw-p-4">
                {brandData.historical_data &&
                brandData.historical_data.length > 0 ? (
                  <div className="tw-h-80 tw-relative">
                    <canvas ref={sellersChartRef}></canvas>
                  </div>
                ) : (
                  <div className="tw-flex tw-items-center tw-justify-center tw-h-80">
                    <p className="tw-text-gray-500 tw-mb-0">
                      Недостаточно данных для отображения графика
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandAnalyticsDisplay;
